import React from 'react';
import { RISK_ANCHORS } from './riskAnchors';

/**
 * RiskQuestionStep — renders a list of risk questions as fieldset/legend groups,
 * each showing a plain-English question (RISK_ANCHORS[key].question) and its five
 * concrete, ordered answer options (RISK_ANCHORS[key].options — index 0 = weakest
 * business ... index 4 = strongest business).
 *
 * The stored value is still the option's INDEX, an integer 0..4, exactly as
 * before (Task 2 of docs/superpowers/plans/2026-08-18-wizard-plain-language.md
 * only changed the presentation, not the emitted data shape — backend/mvaSchema.js,
 * the engine, and the chat handoff all depend on that).
 *
 * Props:
 *   questions — array of { key, label, help } (from liteQuestions.js RISK_QUESTIONS)
 *   answers   — object { [key]: integer|null }
 *   errors    — object { [key]: string|undefined }
 *   onAnswer  — (key: string, value: integer) => void
 *   notes     — optional object { [key]: string }. When present, the assistant's
 *               one-line justification for that rating (services/intakeAgent.js
 *               runTurn's riskNotes) renders as muted helper text beneath the
 *               question. A key absent from notes renders nothing — no placeholder.
 *               Only ConfirmSummary passes this; LiteWizard's plain form-fill
 *               flow has no justification to show, so it's optional and defaults
 *               to {} to leave LiteWizard's rendering unchanged.
 *
 * Ordering: options render in RISK_ANCHORS array order (index 0 first, index 4
 * last) — never re-sorted or reversed. Getting this backwards would silently
 * invert every valuation with no test failure; see riskAnchors.js's INVARIANT
 * comment and this file's neighbouring RiskQuestionStep.test.js, which pins the
 * on-screen order to the RISK_ANCHORS array order.
 *
 * Accessibility: each option is a real radio input wrapped in a <label>, so its
 * accessible name is exactly its visible option text (WCAG 2.5.3 "Label in
 * Name") — no aria-label needed, and the option remains reachable and
 * selectable with the keyboard alone (Tab between fieldsets, arrow keys move
 * within a radio group, Space selects).
 */
function RiskQuestionStep({ questions, answers, errors, onAnswer, notes = {} }) {
  return (
    <div className="lite-wizard-risk-step">
      {questions.map((q) => {
        const anchor = RISK_ANCHORS[q.key];
        return (
          <fieldset
            key={q.key}
            className={`lite-wizard-question-group${errors && errors[q.key] ? ' lite-wizard-question-group--error' : ''}`}
          >
            <legend className="lite-wizard-question-legend">
              <span className="lite-wizard-question-kicker">{q.label}</span>
              <span className="lite-wizard-question-text">{anchor.question}</span>
            </legend>
            {q.help && (
              <p className="lite-wizard-question-help">{q.help}</p>
            )}
            {notes[q.key] && (
              <p className="lite-wizard-question-note">{notes[q.key]}</p>
            )}
            <div className="lite-wizard-option-list">
              {anchor.options.map((optionText, idx) => (
                <label
                  key={idx}
                  className={`lite-wizard-option${answers[q.key] === idx ? ' lite-wizard-option--selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={`risk-${q.key}`}
                    value={idx}
                    checked={answers[q.key] === idx}
                    onChange={() => onAnswer(q.key, idx)}
                    className="lite-wizard-option-input"
                  />
                  <span className="lite-wizard-option-text">{optionText}</span>
                </label>
              ))}
            </div>
            {errors && errors[q.key] && (
              <span className="lite-wizard-field-error">{errors[q.key]}</span>
            )}
          </fieldset>
        );
      })}
    </div>
  );
}

export default RiskQuestionStep;
